import { useState } from 'react';
import { Stack, Title, Card, PasswordInput, Button, Group, Text, Alert } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconLock, IconAlertCircle } from '@tabler/icons-react';
import { api } from '../../api/client.ts';

/**
 * Change password. Mostly useful for the seeded admin/user accounts (form
 * login). Google-OAuth users have no local password — they should be told
 * so rather than left clicking "Save" on a no-op.
 */
export function ChangePasswordPage() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 4 || next.length > 100) {
      notifications.show({ message: 'New password must be 4–100 characters', color: 'red' });
      return;
    }
    if (next !== confirm) {
      notifications.show({ message: 'Passwords do not match', color: 'red' });
      return;
    }
    setSubmitting(true);
    try {
      // The legacy endpoint takes the password as the raw request body
      // (Content-Type: text/plain), not JSON. It does NOT verify the current
      // password — preserving that behaviour exactly to match the server.
      const res = await api.post('/api/account/change-password', next, {
        headers: { 'Content-Type': 'text/plain' },
      });
      if (res.status === 200) {
        notifications.show({ message: 'Password changed', color: 'green' });
        setCurrent('');
        setNext('');
        setConfirm('');
      } else {
        notifications.show({ message: `Failed: ${res.status}`, color: 'red' });
      }
    } catch (err) {
      notifications.show({
        message: 'Failed' + (err instanceof Error ? `: ${err.message}` : ''),
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack gap="md">
      <Title order={2}>Change password</Title>
      <Card withBorder padding="md" radius="md" maw={520}>
        <Stack gap="sm">
          <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
            <Text size="sm">
              If you signed in via Google, you don't have a local password — this form is only
              useful for accounts created with a username and password.
            </Text>
          </Alert>
          <form onSubmit={submit}>
            <Stack gap="sm">
              <PasswordInput
                label="Current password"
                value={current}
                onChange={e => setCurrent(e.currentTarget.value)}
                description="The server does not verify this — it's here for your own confirmation."
              />
              <PasswordInput
                label="New password"
                required
                value={next}
                onChange={e => setNext(e.currentTarget.value)}
                minLength={4}
                maxLength={100}
              />
              <PasswordInput
                label="Confirm new password"
                required
                value={confirm}
                onChange={e => setConfirm(e.currentTarget.value)}
                error={confirm && confirm !== next ? 'Does not match' : undefined}
              />
              <Group justify="flex-end" mt="sm">
                <Button type="submit" leftSection={<IconLock size={16} />} loading={submitting}>
                  Change password
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Card>
    </Stack>
  );
}
